import BaseApiService from '../../auditor/service/baseApiService.mjs';

/**
 * Clarification questions: a reviewer asks someone further down the approval
 * chain about a claim or a report, and they answer here instead of the item
 * being sent back. The server decides who may see, ask, answer and resolve
 * (backend src/clarifications/clarificationAccess.mjs); this only carries the
 * requests.
 */
const toError = (error, fallback) => {
  const e = new Error(error.response?.data?.message || fallback);
  e.status = error.response?.status;
  return e;
};

/** Files go with the message they belong to, as one multipart request. */
const toForm = (fields, files = []) => {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) form.append(k, String(v));
  });
  files.forEach((f) => form.append('files', f));
  return form;
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

class ClarificationService extends BaseApiService {
  /**
   * Threads the caller can see.
   * @param {object} [params] { status: 'unresolved'|'open'|'answered'|'resolved'|'closed'|'any',
   *                            scope: 'all'|'mine'|'addressed', reportId, claimType, claimId, region }
   */
  async list(params = {}) {
    try {
      const response = await this.api.get('/clarifications', { params });
      return response.data?.data || [];
    } catch (error) {
      throw toError(error, 'Failed to load questions');
    }
  }

  /** { count, needsAnswer } for the bell. */
  async unreadCount() {
    try {
      const response = await this.api.get('/clarifications/unread-count');
      return response.data?.data || { count: 0, needsAnswer: 0 };
    } catch (error) {
      throw toError(error, 'Failed to count questions');
    }
  }

  /** May I ask about this item, and whom? { canAsk, reason, options, subjectLabel } */
  async askOptions({ claimType, claimId, reportId }) {
    try {
      const response = await this.api.get('/clarifications/ask-options', {
        params: claimId ? { claimType, claimId } : { reportId },
      });
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to check who can be asked');
    }
  }

  /** One thread: { thread, messages, subject, permissions, transcriptSha256 } */
  async get(threadId) {
    try {
      const response = await this.api.get(`/clarifications/${threadId}`);
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to load the question');
    }
  }

  /** Ask a question about a claim (claimType + claimId) or a report (reportId). */
  async ask({ claimType, claimId, reportId, directedTo, body, files = [] }) {
    try {
      const response = await this.api.post(
        '/clarifications',
        toForm({ claimType, claimId, reportId, directedTo, body }, files),
        MULTIPART
      );
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to send the question');
    }
  }

  async reply(threadId, { body, files = [] }) {
    try {
      const response = await this.api.post(`/clarifications/${threadId}/messages`, toForm({ body }, files), MULTIPART);
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to send the message');
    }
  }

  async redirect(threadId, directedTo) {
    try {
      const response = await this.api.post(`/clarifications/${threadId}/redirect`, { directedTo });
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to pass the question on');
    }
  }

  async resolve(threadId, note = '') {
    try {
      const response = await this.api.post(`/clarifications/${threadId}/resolve`, { note });
      return response.data?.data;
    } catch (error) {
      throw toError(error, 'Failed to resolve the question');
    }
  }

  /** Best-effort: a failed read mark only means the bell stays lit a little longer. */
  async markRead(threadId) {
    try {
      await this.api.post(`/clarifications/${threadId}/read`);
    } catch {
      /* ignored */
    }
  }
}

const clarificationService = new ClarificationService();
export default clarificationService;
